<!--
  The language switcher, as V1 had it: a globe and the current flag in the header.

  A real menu rather than a toggle, because two languages today does not mean two
  languages forever, and a toggle that silently becomes a cycle is a worse control than a
  list that was always a list.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ICONS } from '@shiocast/ui';
import { LOCALES, LOCALE_FLAGS, LOCALE_NAMES, setLocale, type Locale } from '@/i18n';

const { t, locale } = useI18n();
const open = ref(false);

const current = computed(() => locale.value as Locale);

function choose(next: Locale): void {
  setLocale(next);
  open.value = false;
}
</script>

<template>
  <div class="lang" @keydown.esc="open = false">
    <button
      class="shio-btn shio-btn--ghost lang__toggle"
      type="button"
      :aria-label="t('common.language')"
      :aria-expanded="open"
      aria-haspopup="menu"
      @click="open = !open"
    >
      <component :is="ICONS.Globe" :size="16" aria-hidden="true" />
      <span class="lang__flag" aria-hidden="true">{{ LOCALE_FLAGS[current] }}</span>
    </button>

    <ul v-if="open" class="lang__menu" role="menu" :aria-label="t('common.language')">
      <li v-for="option in LOCALES" :key="option" role="none">
        <button
          class="lang__option"
          type="button"
          role="menuitemradio"
          :aria-checked="option === current"
          @click="choose(option)"
        >
          <span aria-hidden="true">{{ LOCALE_FLAGS[option] }}</span>
          <span>{{ LOCALE_NAMES[option] }}</span>
          <component v-if="option === current" :is="ICONS.Check" :size="14" aria-hidden="true" />
        </button>
      </li>
    </ul>

    <!-- Closes the menu on any outside click, without trapping focus: this is a
         two-item picker, not a dialog. -->
    <div v-if="open" class="lang__scrim" @click="open = false"></div>
  </div>
</template>

<style scoped>
.lang {
  position: relative;
}

.lang__toggle {
  display: flex;
  align-items: center;
  gap: var(--shio-space-1);
}

.lang__flag {
  font-size: var(--shio-type-body-sm-size);
}

.lang__menu {
  position: absolute;
  top: calc(100% + var(--shio-space-1));
  right: 0;
  z-index: var(--shio-z-drawer);
  min-width: 11rem;
  margin: 0;
  padding: var(--shio-space-1);
  list-style: none;
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-elevated));
  box-shadow: 0 8px 24px hsl(0 0% 0% / 0.5);
}

.lang__option {
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
  width: 100%;
  padding: var(--shio-space-2);
  border: 0;
  border-radius: var(--shio-radius-sm);
  background: transparent;
  color: hsl(var(--shio-text-primary));
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-body-sm-size);
  text-align: left;
  cursor: pointer;
}

.lang__option:hover {
  background: hsl(var(--shio-surface-base));
}

.lang__option:focus-visible {
  outline: 2px solid hsl(var(--shio-accent-cyan));
  outline-offset: -2px;
}

.lang__scrim {
  position: fixed;
  inset: 0;
  z-index: calc(var(--shio-z-drawer) - 1);
}
</style>
